import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
} from '@angular/core';

import { startWith } from 'rxjs';

import type { User } from '@core/account-types';
import { AdminsAvatarsComponent } from '@core/account-ui';
import { filterBoolean } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  DropdownComponent,
  DropdownItemComponent,
  IconButtonComponent,
  IconComponent,
  NestedTemplateTriggerDirective,
  RtTooltip,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { ViewForm } from '@core/views-types';
import type { RoleWithAdmins } from '@core/visibility-legacy';
import {
  VisibilitySelectorDropdownComponent,
  VisibilitySelectorService,
} from '@core/visibility-legacy';
import { VisibilityLevel } from '@core/visibility-types';

import { ViewsAnalytics } from '../../../analytics/views-analytics';
import { injectViewsVisibilityLabels } from '../../consts';

@Component({
  selector: 'view-settings-visibility',
  imports: [
    IconButtonComponent,
    DropdownItemComponent,
    RtTooltip,
    NestedTemplateTriggerDirective,
    IconComponent,
    AdminsAvatarsComponent,
    TranslatePipe,
    VisibilitySelectorDropdownComponent,
  ],
  templateUrl: './view-settings-visibility.component.html',
  styleUrl: './view-settings-visibility.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewSettingsVisibilityComponent {
  protected readonly VisibilityLevel = VisibilityLevel;

  public readonly visibilityLabels = injectViewsVisibilityLabels();

  public visibilityControl = input.required<ViewForm['controls']['visibility']>();
  public isViewPredefined = input.required<boolean>();

  public mainDropdown = inject(DropdownComponent);
  public viewsAnalytics = inject(ViewsAnalytics);
  public visibilityAvatarMaxLength = 3;
  public visibilityUsersData?: { admins: User[]; rolesWithAdmins: RoleWithAdmins[] };
  public allowAdminsChange: boolean;

  private visibilitySelectorService = inject(VisibilitySelectorService);
  private cdRef = inject(ChangeDetectorRef);
  private untilDestroyed = injectUntilDestroyed();

  public ngOnInit() {
    this.watchVisibility();
  }

  public onVisibilityAvatarsClick(event: MouseEvent) {
    event.stopPropagation();
    this.onVisibilityChanges(VisibilityLevel.Protected);
  }

  public onVisibilityChanges(level: VisibilityLevel) {
    const visibility = this.visibilityControl().getRawValue();
    this.visibilitySelectorService
      .onVisibilityChanges(level, visibility, { roleSelectionRequired: true })
      .pipe(this.untilDestroyed)
      .subscribe(value => {
        this.mainDropdown.prevTemplate();
        if (value) {
          this.visibilityControl().markAsDirty();
          this.visibilityControl().setValue(value);
        }
        this.cdRef.detectChanges();
      });
  }

  private watchVisibility() {
    const visibility = this.visibilityControl();
    this.visibilitySelectorService
      .getVisibilityUsersData(
        visibility.valueChanges.pipe(startWith(visibility.value), filterBoolean),
      )
      .pipe(this.untilDestroyed)
      .subscribe(usersData => {
        this.visibilityUsersData = usersData;
        this.cdRef.detectChanges();
      });

    this.visibilitySelectorService.featureVisibility$
      .pipe(this.untilDestroyed)
      .subscribe(featureVisibility => {
        this.allowAdminsChange = featureVisibility;
        this.cdRef.detectChanges();
      });
  }
}
